import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { esCamion } from '@abastece/shared';
import { responsableDe, type SesionCaja } from '@abastece/core';
import { db } from '../db/dexie.js';
import { sesionAbierta } from '../lib/caja.js';
import { useSesion } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';

/**
 * The counter's till open on this device when it is somebody else's — opened by them, or
 * received by them last — with that person's name. Null when it is the signed-in person's, when
 * there is none, and on a truck, whose till changes hands only at the settlement. Undefined while
 * it is read: taken for none, «Recibir la caja» said there was nothing to receive every time it
 * opened, for a moment.
 */
export function useCajaDeOtro(): { caja: SesionCaja; nombre: string } | null | undefined {
  const sesion = useSesion();
  return useLiveQuery(async () => {
    const caja = await sesionAbierta();
    if (!caja || !sesion || esCamion(caja.ubicacion) || responsableDe(caja) === sesion.usuarioId) return null;
    const nombre = (await db.usuarios.get(responsableDe(caja)))?.nombre ?? '';
    return { caja, nombre };
  }, [sesion?.usuarioId]);
}

/**
 * Said where the till is used — the counter, «Caja», the home screen — while it is somebody
 * else's: whose it is, and the way to take it over (`routes/RecibirLaCaja.tsx`, counted a step at
 * a time).
 */
export function AvisoCajaDeOtro({ nombre }: { nombre: string }) {
  const t = useT();
  return (
    <div className="tarjeta flex flex-col gap-3 ring-amber-500/30">
      <p className="text-sm text-amber-200">{t('entrega.aviso', { nombre })}</p>
      <Link to="/caja/recibir" className="btn-primario">
        <Ico icono="contar" />
        {t('entrega.recibirDe', { nombre })}
      </Link>
    </div>
  );
}
